import { Component, DestroyRef, ElementRef, inject, ChangeDetectionStrategy, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GsSegmentBarItem } from './models/segment-bar-item';

@Component({
  template: `
    <ng-content></ng-content>
  `,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: GsSegmentBarController,
    },
  ],
})
export class GsSegmentBarController implements ControlValueAccessor {
  // Services
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // Inputs
  /** List of items to show as options */
  readonly items = input<GsSegmentBarItem[]>([]);
  /** Prefix to use for translation keys */
  readonly translatePrefix = input<string | undefined>('');

  // Properties
  /** The currently selected item */
  protected selectedItem: FormControl<string | null> = new FormControl<string>('');

  // Methods
  /**
   * Handles keyboard navigation for tabs (WCAG 2.1 AA compliance)
   * - ArrowLeft/ArrowRight: Navigate between tabs
   * - Home: Go to first tab
   * - End: Go to last tab
   * - Enter/Space: Activate tab (handled by click)
   */
  protected onKeydown(event: KeyboardEvent, currentIndex: number): void {
    const enabledIndices = this.items().map((item, i) => (!item.disabled ? i : -1)).filter((i) => i !== -1);
    const currentEnabledIndex = enabledIndices.indexOf(currentIndex);

    let targetIndex: number | null = null;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        targetIndex = currentEnabledIndex > 0 ? enabledIndices[currentEnabledIndex - 1] : enabledIndices[enabledIndices.length - 1];
        break;
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = currentEnabledIndex < enabledIndices.length - 1 ? enabledIndices[currentEnabledIndex + 1] : enabledIndices[0];
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = enabledIndices[0];
        break;
      case 'End':
        event.preventDefault();
        targetIndex = enabledIndices[enabledIndices.length - 1];
        break;
    }

    if (targetIndex !== null && targetIndex !== currentIndex) {
      const targetItem = this.items()[targetIndex];
      this.writeValue(targetItem.value);
      this.focusTab(targetIndex);
    }
  }

  /** Focuses the tab button at the given index */
  private focusTab(index: number): void {
    const buttons = this.elementRef.nativeElement.querySelectorAll('[role="tab"]');
    if (buttons[index]) {
      (buttons[index] as HTMLElement).focus();
    }
  }

  writeValue(val: string): void {
    const foundItem = this.items().find((item) => item.value === val);
    if (foundItem && !foundItem.disabled) this.selectedItem.setValue(val);
  }
  registerOnChange(fn: (value: string | null) => void): void {
    this.selectedItem.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fn);
  }
  registerOnTouched(fn: (value: string | null) => void): void {
    this.selectedItem.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fn);
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedItem.disable({ emitEvent: false });
    } else {
      this.selectedItem.enable({ emitEvent: false });
    }
  }
}
